import baseDebug from 'debug';

const PREFIX = 'aline:';

/**
 * Creates a namespaced debugger under the `aline:` prefix.
 *
 * Prefer adding new named debuggers in `./loggers.ts` and importing the
 * exported binding rather than calling this factory at the call site —
 * `loggers.ts` is the single place that lists every available namespace.
 *
 * Disabled namespaces are a near-zero-cost no-op (a single boolean check per
 * call), so it's safe to leave call sites in code.
 *
 * Toggling:
 * - From code (scratchpad): see `runtime-overrides.ts`.
 * - Server env var:        `DEBUG=aline:redline:* pnpm dev`
 * - Browser DevTools:      `localStorage.debug = 'aline:redline:*'` (then refresh)
 *
 * Wildcards (`aline:redline:*`) and negation (`-aline:redline:layering`) work
 * as in standard `debug` filter syntax.
 */
export function debug(namespace: string): baseDebug.Debugger {
  return baseDebug(`${PREFIX}${namespace}`);
}

/**
 * Programmatically enables a set of debug namespaces (comma-separated,
 * wildcards/negations supported). Useful as a scratchpad to flip
 * namespaces on without touching env vars or DevTools.
 *
 * NOTE: in the browser, this also persists to `localStorage.debug` — if
 * you remove a call to `enableDebug` and want to fully clear browser
 * overrides, run `localStorage.removeItem('debug')` in DevTools.
 */
export function enableDebug(namespaces: string): void {
  baseDebug.enable(namespaces);
}

/**
 * Disables all debug output and clears any persisted browser override.
 */
export function disableDebug(): void {
  baseDebug.disable();
}
